import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Link, matchPath, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import ReactDOM from "react-dom";
import {Helmet} from "react-helmet";
import {useAuth} from "../../providers/AuthProvider";
import {useTalentTeam} from "../../providers/TalentTeamProvider";
import LoadingBanner from "../../components/LoadingBanner";
import PageNotFound from "../../components/errors/PageNotFound";
import TalentTeamInfoStep from "./components/TalentTeamInfoStep";
import TeamServicesStep from "./components/TeamServicesStep";
import TeamRosterStep from "./components/TeamRosterStep";
import TeamNameForm from "./components/TeamNameForm";
import TalentTeamManage from "./TalentTeamManage";
import PageNotAllowed from "../../components/errors/PageNotAllowed";

const TalentTeamSetup = (props) => {
    const [queryParams, setQueryParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const routeRef = useRef(null);
    const formRef = useRef(null);

    const {auth, appropriate, logout} = useAuth();

    const [isDisabled, setIsDisabled] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);

    const [stepSubmitDisabled, setStepSubmitDisabled] = useState(false);

    const {
        team,
        isLoading,
        isAuthOwner,
        isAuthAdmin,
        updateTeamDetails,
        teamResponse,
        createTeam,
        isUpdating,
    } = useTalentTeam();

    const asTalentManager = location.pathname.includes('manager')

    const teamType = useMemo(() => {
        if (auth.canManageTalent()) {
            return asTalentManager ? 'talent/manager' : 'talent';
        }
        return auth?.canManageClientResourceTalent() ? 'supplier' : 'talent'
    }, [auth]);

    const isSetup = useMemo(() => {
        return matchPath({path: `${teamType}/team/setup/*`},location.pathname)
    }, [location?.pathname]);

    const isManaging = useMemo(() => {
        return matchPath({path: `${teamType}/team/manage/:teamId/*`},location.pathname)
    }, [location?.pathname]);

    const canManageTeam = useMemo(() => team?.canManageTeamProfile?.(), [auth, team, isAuthAdmin, isAuthOwner]);

    const positionsInvites = useMemo(() => {
        return (teamResponse?.positions || []).length + (teamResponse?.pending_invites || []).length
    }, [isUpdating, teamResponse]);

    const totalServiceGroups = useMemo(() => {
        return teamResponse?.talent_service_groups?.length
    }, [isUpdating, teamResponse]);
    const strippedBio = useMemo(() => {
        return TB.stripHtml(teamResponse?.bio)
    }, [isUpdating, teamResponse]);

    const activeSteps = useMemo(() => {

        return [
            {
                path: 'project-types',
                label: `Project Types ${totalServiceGroups > 0 ? `(${totalServiceGroups})`: ''}`,
                isCompleted: totalServiceGroups > 0,
                icon: 'fas fa-link'
            },
            // {path: 'links-documents', label: 'Links & Documents', isCompleted: true, icon: 'fas fa-link'},
            // {path: 'work-samples', label: 'Work Samples', isCompleted: true, icon: 'fas fa-briefcase'},
            // {path: 'awards-certifications', label: 'Awards & Certifications', isCompleted: true, icon: 'fas fa-trophy'},
            {
                path: 'roster',
                label: `Team Roster ${positionsInvites > 1 ? `(${positionsInvites})`: ''}`,
                isCompleted: positionsInvites > 1,
                icon: 'fas fa-users'
            },
            {
                path: 'details',
                label: 'Team Bio',
                isCompleted: teamResponse?.id && strippedBio?.length > 0,
                icon: 'fas fa-info-circle'
            },
        ]
    }, [
        isUpdating,
        positionsInvites,
        totalServiceGroups,
        strippedBio,
        teamResponse
    ]);

    const isTeamSetupCompleted = useMemo(() => {
        if (!teamResponse?.id) return false;
        return !TB.find(activeSteps, ['isCompleted', false])
    }, [
        isUpdating,
        teamResponse,
        TB.map(activeSteps, 'isCompleted')
    ])

    const [lastSavedStep, setLastSavedStep] = useState(() => activeSteps[0])

    const currentStepIndex = useMemo(() => {
        return TB.findIndex(activeSteps, (step, index) => {
            return  location.pathname.includes(step.path)
        })
    }, [location?.pathname, activeSteps]);

    const currentStep = useMemo(() => {
        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    const nextStep = useMemo(() => {
        if (currentStepIndex !== -1) {
            return activeSteps[currentStepIndex + 1]
        }
        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    const prevStep = useMemo(() => {
        if (currentStepIndex !== -1) {
            return activeSteps[currentStepIndex - 1]
        }

        return activeSteps[currentStepIndex]
    }, [currentStepIndex, activeSteps]);

    useEffect(() => {
        if (isManaging && team && team.canManageTeamProfile() && !isTeamSetupCompleted) {

            return navigate(`/${teamType}/team/setup/${team?.id}`, {replace: true})
        }
        if (!canManageTeam || !team || currentStepIndex !== -1) {
            return;
        }
        if ((isTeamSetupCompleted || isManaging) && location.pathname.includes('control-center')) {
            return;
        }
        if (!lastSavedStep) {
            // do something else??
        }

        return goToStep(lastSavedStep, true)
    }, [team, currentStepIndex])

    const onSubmit = async () => {
        if (isUpdating || !isFormComplete) return;

        if (formRef?.current) {
            let invalid = formRef.current.forceValidate();

            console.log('onSubmitinvalid', invalid)
            if (invalid) {
                const node = ReactDOM.findDOMNode(invalid);
                return $('html, body').animate({scrollTop: $(node).offset().top - 50}, 800);
            }
        }

        let didUpdate;

        switch (currentStep?.path) {
            case 'details':
                didUpdate = await updateTeamDetails(team)
                break;

            case 'project-types':
            case 'roster':
                didUpdate = true
                break;
        }
        // }else {
        //     let newTeam = await createTeam({});
        //
        //     if (newTeam?.id && nextStep) {
        //         return navigate(
        //             location.pathname.replace('new/details',`${newTeam.id}/${nextStep.path}`),
        //             {replace: true}
        //         )
        //     }
        //     return;
        // }

        if(!didUpdate) return;

        let pathname =  `control-center`;

        if (isSetup) {
            pathname = `./../../manage/${team?.id}/control-center`;

            if (nextStep) {
                pathname = nextStep.path;
            }
        }

        return navigate({
            ...location,
            pathname
        }, {
            replace: true,
            state: location.state || {}
        })
    }

    const isFormComplete = useMemo(() => {
        if (!team || isLoading) return true;

        let validations = {
            details: team.bio,
            ['project-types']: team.talent_service_groups?.length,
            roster: team.positions?.length > 0 || team.pending_invites?.length > 0,
        }
        if (currentStep) {
            return validations[currentStep.path];
        }
        let invalid = false;

        return !invalid
    }, [currentStep,
        isLoading,
        team?.name,
        team?.bio,
        team?.banner_image_path,
        team?.talent_service_groups,
        team?.positions,
    ]);

    const goBack = async () => {
        // await onFormReset();
        if (isManaging) {
            return navigate({
                ...location,
                pathname: `./../`
            }, {
                replace: true
            })
        }

        if (currentStep && prevStep) {
            let pathname = prevStep.path;
            return navigate({
                ...location,
                pathname
            }, {
                replace: true,
                state: location.state || {}
            })
        }
    }

    const goToStep = (step, replace = false) => {
        let route = {
            ...location,
            pathname: step.path
        }
        if (replace) {
            return navigate(route, {replace: true});
        }
        return navigate(route)
    }

    const toggleDisabled = (submitDisabled) => {
        return setStepSubmitDisabled(submitDisabled)
    }

    // if (!currentStep && isManaging && canManageTeam && (team && !location.pathname.includes('control-center'))) {
    //     return null
    // }

    let isLastStep = currentStepIndex === activeSteps.length - 1;

    if (auth?.isTalentSupplierResource()) {
        return <PageNotAllowed />
    }
    if (isLoading || !team) {
        return <LoadingBanner message="Loading" adjustOverlay/>;
    }

    return (
        <React.Fragment>
            {currentStep?.label && (
                <Helmet>
                    <title>{currentStep.label} {(isSetup || !team.name) ? '- Setup New Team' : `for ${team.name}`} | Wripple</title>
                </Helmet>
            )}
            <div className="talent-application-container team-application-container">
                <div className="talent-application-form-container">
                    {canManageTeam &&
                        <div className="talent-app-nav p-0 basis-full lg:basis-3/12 xl:basis-2/12 overflow-hidden"
                             style={{borderTop: '1px solid #E5E5E5'}}>
                            <div className="bg-black flex flex-row items-center h-[45px] mb-1">
                                <Link
                                    style={{ paddingLeft: 35 }}
                                    to={`/${teamType}/teams`}
                                    className="text-xs text-center btn btn-link btn-link-white mx-auto no-underline px-1 hover:text-white focus:text-white"
                                >
                                    <i className="fas fa-arrow-left mr-1"/>&nbsp;Back to Teams
                                </Link>
                            </div>
                            <h3 className="font-bold px-1.5 text-center break-words">
                                {(isSetup || !team.name) ? 'Setup Team' : `Manage Team`}
                            </h3>

                            {activeSteps.map(step => (
                                <button
                                    key={step.path}
                                    type="button"
                                    className={TB.mapClass('step-title', {
                                        'isCompleted': step.isCompleted,
                                        'isActive': location.pathname.includes(step.path),
                                    })}
                                    disabled={isUpdating || isEditingName}
                                    onClick={() => navigate(step.path)}
                                >
                                    <i className={TB.mapClass("fas", {
                                        "fa-check-circle": step.isCompleted,
                                        "fa-times-circle": team?.id && !step.isCompleted,
                                        "fa-arrow-alt-circle-right": !team.id && !step.isCompleted,
                                    })}/>
                                    {step.label}
                                </button>
                            ))}


                            <hr/>
                            <button
                                type="button"
                                className={TB.mapClass('step-title', {
                                    'isActive': location.pathname.includes('control-center'),
                                    'opacity-60 disabled pointer-events-auto cursor-pointer': (canManageTeam && !isTeamSetupCompleted && !location.pathname.includes('control-center')),
                                })}
                                disabled={isUpdating || isEditingName}
                                onClick={(e) => {
                                    if(canManageTeam && !isTeamSetupCompleted && !location.pathname.includes('control-center')) {
                                        e.preventDefault();
                                        return TB.toast('Team Control Center can be viewed after setup is completed', {theme: 'danger'})
                                    }
                                    return navigate(team.detailLink(asTalentManager));
                                }}
                            >
                                <i className="fas fa-cog text-lg"/>
                                Team Control Center
                            </button>

                        </div>
                    }
                    <div className={TB.mapClass("basis-full overflow-hidden", {
                        "lg:basis-9/12 xl:basis-10/12": canManageTeam
                    })}>
                        {canManageTeam && (
                            <div className="px-[35px] mb-6 xl:w-10/12">
                                <div className="border-b border-b-gray-300 py-4 pb-1">
                                    <TeamNameForm isEditing={isEditingName} isDisabled={isDisabled} onToggleEdit={setIsEditingName}/>
                                </div>
                            </div>
                        )}

                        <div className={TB.mapClass("talent-application pt-0 px-4 md:px-8 w-full mr-auto ml-0", {
                            "xl:w-10/12": !location.pathname.includes('roster') && !location.pathname.includes('control-center'),
                            "pointer-events-none opacity-50": isEditingName,
                            "pt-0": canManageTeam,
                            "pt-3": !canManageTeam,
                        })}>


                            <Routes>
                                <Route index element={<Navigate to={isManaging ? "control-center" : "project-types"} replace/>}/>
                                {(team && canManageTeam) && (
                                    <React.Fragment>
                                        <Route path="project-types" element={<TeamServicesStep isSetup={isSetup} isManaging={isManaging}/>}/>
                                        <Route path="roster" element={<TeamRosterStep isSetup={isSetup} isManaging={isManaging}/>}/>
                                        <Route path="details" element={<TalentTeamInfoStep ref={formRef} isSetup={isSetup} isManaging={isManaging}/>}/>
                                    </React.Fragment>
                                )}
                                <Route path="control-center" element={<TalentTeamManage/>}/>

                                <Route path="*" element={(team && !canManageTeam && (currentStep || isSetup)) ? <Navigate to={team.detailLink(asTalentManager)} replace/>: <PageNotFound/>}/>
                            </Routes>

                            {/*<div className="absolute inset-0 flex flex-col z-40 items-center pt-52"></div>*/}
                            {/*<FirstTimeTeamSetupModal/>*/}
                        </div>
                    </div>
                </div>


                <div className="talent-app-footer">
                    {!location.pathname.includes('invite') && (
                        <div className="flex flex-grow flex-row items-center">
                            {/*{(isSetup || currentStepIndex === 0) && (*/}
                            {/*    <button*/}
                            {/*        type="button"*/}
                            {/*        onClick={() => {*/}
                            {/*            if (isSetup) return navigate(`/${teamType}/teams/all`);*/}
                            {/*            if (isManaging) return navigate('./../')*/}
                            {/*        }}*/}
                            {/*        disabled={isDisabled || stepSubmitDisabled}*/}
                            {/*        className="btn btn-outline-dark">*/}
                            {/*        Cancel*/}
                            {/*    </button>*/}
                            {/*)}*/}
                            <div className="flex-grow flex flex-row items-center justify-end">
                                {(currentStepIndex > 0 && prevStep) && (
                                    <button
                                        type="button"
                                        onClick={() => goToStep(prevStep, true)}
                                        disabled={isDisabled || isUpdating || stepSubmitDisabled}
                                        className="btn btn-outline-dark">
                                        Go Back
                                    </button>
                                )}

                                {nextStep && (
                                    <button
                                        type="button"
                                        disabled={isDisabled || isUpdating || stepSubmitDisabled || !isFormComplete}
                                        onClick={() => onSubmit()}
                                        className="btn btn-primary ml-4">
                                        Save & Continue
                                    </button>
                                )}

                                {isLastStep && (
                                    <button
                                        type="button"
                                        disabled={isDisabled || isUpdating || stepSubmitDisabled || !isFormComplete}
                                        onClick={() => onSubmit()}
                                        className="btn btn-primary ml-4">
                                        Submit
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </React.Fragment>
    );
}


export default TalentTeamSetup;